import { Injectable, Signal, signal } from '@angular/core';
import { SortModel } from '@common/models/sort.model';
import { AssetFilterFormValueModel } from '@shared/models/assets/asset-filter-form-value.model';
import { SearchMode } from '@shared/models/assets/search-mode.model';
import { AssetViewMode } from '@shared/models/assets/asset-view-mode.model';

@Injectable({ providedIn: 'root' })
export class AssetQueryStore {
  readonly #defaultFilterCriteria: AssetFilterFormValueModel = {
    ownership: {
      unitIds: [],
      subUnitIds: [],
      tagIds: []
    },
    contentClassification: {
      mediaTypeIds: [],
      modeLearningIds: [],
      subjectIds: [],
      topicIds: [],
      subTopicIds: []
    },
    intendedAudience: {
      primaryAudienceIds: [],
      modeEngagementIds: [],
      programAdministrationIds: [],
      languageIds: [],
      durationIds: [],
      accessibilityStandardIds: []
    },
    educationAlignment: {
      gradeLevelIds: [],
      ageRangeIds: [],
      skillIds: []
    }
  };
  readonly #filterCriteria = signal<AssetFilterFormValueModel>(this.#defaultFilterCriteria);
  readonly #searchTerm = signal<string>('');
  readonly #searchMode = signal<SearchMode>('Simple');
  readonly #defaultView = signal<AssetViewMode>('list');
  readonly #sortingModel = signal<SortModel>({ field: 'updatedAt', order: 'desc' });
  readonly #filterAssetStatus = signal<AssetStatusModel[]>(['Published', 'Draft']);
  readonly #assetUnit = signal<Maybe<ID>>(undefined);
  readonly #filterBatchId = signal<ID>('');

  public readonly sortingModel = this.#sortingModel.asReadonly();
  public readonly defaultView = this.#defaultView.asReadonly();
  public readonly searchTerm = this.#searchTerm.asReadonly();
  public readonly searchMode = this.#searchMode.asReadonly();
  public readonly filterCriteria = this.#filterCriteria.asReadonly();
  public readonly filterAssetStatus = this.#filterAssetStatus.asReadonly();
  public readonly assetUnit = this.#assetUnit.asReadonly();
  public readonly filterBatchId: Signal<ID> = this.#filterBatchId.asReadonly();

  public saveFilter(data: AssetFilterFormValueModel): void {
    this.#filterCriteria.set(data);
  }

  public saveSearchTerm(searchTerm: string): void {
    this.#searchTerm.set(searchTerm);
  }

  public saveSearchMode(value: SearchMode): void {
    this.#searchMode.set(value);
  }

  public saveView(view: AssetViewMode): void {
    this.#defaultView.set(view);
  }

  public saveSort(sort: SortModel): void {
    this.#sortingModel.set(sort);
  }

  public saveFilterAssetStatus(status: AssetStatusModel[]): void {
    this.#filterAssetStatus.set(status);
  }

  public saveAssetUnit(value: ID): void {
    this.#assetUnit.set(value);
  }

  public saveFilterBatchId(value: ID): void {
    this.#filterBatchId.set(value);
  }

  public reset(): void {
    this.#filterCriteria.set(this.#defaultFilterCriteria);
    this.#searchTerm.set('');
    this.#defaultView.set('list');
    this.#searchMode.set('Simple');
    this.#sortingModel.set({ field: 'updatedAt', order: 'desc' });
    this.#filterAssetStatus.set(['Published', 'Draft']);
    this.#assetUnit.set(undefined);
    this.#filterBatchId.set('');
  }
}
